import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColDef } from 'ag-grid-community';
import { searchSchools } from '../api/schools-api';
import type { SchoolSummary } from '../api/types';
import { Modal } from '../neon/Modal';
import { DataGrid } from './DataGrid';
import { Button, NumberInput, SelectInput } from '../neon/controls';
import { ErrorBanner } from './ui';
import { useDomId } from '../hooks/useDomId';

const RADIUS_OPTIONS = [1, 2, 3, 5, 10, 15, 20, 25, 30, 40, 50].map((n) => ({
  value: n,
  label: `${n} mile${n === 1 ? '' : 's'}`,
}));
const TYPE_OPTIONS = [
  { value: 0, label: 'Public' },
  { value: 1, label: 'Private' },
];
const RELIGIOUS_OPTIONS = [
  { value: 1, label: 'Non-religious' },
  { value: 2, label: 'Religious' },
];

export interface NearbyModalProps {
  nid: string;
  schoolName: string;
  onClose: () => void;
}

/** Legacy "range finder": schools within N miles, filtered by type/religious/enrollment. */
export function NearbyModal({ nid, schoolName, onClose }: NearbyModalProps) {
  const navigate = useNavigate();
  const id = useDomId('nearby');
  const [radius, setRadius] = useState('10');
  const [type, setType] = useState('');
  const [religious, setReligious] = useState('');
  const [minEnrollment, setMinEnrollment] = useState<number | undefined>(undefined);
  const [results, setResults] = useState<SchoolSummary[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const columns = useMemo<ColDef<SchoolSummary>[]>(
    () => [
      { headerName: 'Name', field: 'name', minWidth: 200, flex: 2 },
      { headerName: 'City', field: 'city', minWidth: 120 },
      { headerName: 'State', field: 'state', maxWidth: 90 },
      {
        headerName: 'Miles',
        field: 'distance',
        maxWidth: 90,
        valueFormatter: (p) => (p.value != null ? Number(p.value).toFixed(1) : ''),
      },
      { headerName: 'Enrollment', field: 'enrollment', maxWidth: 120 },
    ],
    [],
  );

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await searchSchools({
        nearNid: nid,
        radiusMiles: radius,
        schoolType: type || undefined,
        religious: religious || undefined,
        minEnrollment,
        limit: 200,
      });
      setResults(res.schools);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={`Find schools near ${schoolName}`}
      size="large"
      onClose={onClose}
      footer={<Button id={`${id}-close`} text="Close" variant="tertiary" onClick={onClose} />}
    >
      <ErrorBanner error={error} />
      <form
        className="search-bar"
        onSubmit={(e) => {
          e.preventDefault();
          void run();
        }}
      >
        <SelectInput id={`${id}-radius`} label="Within" value={radius} onChange={setRadius} options={RADIUS_OPTIONS} />
        <SelectInput id={`${id}-type`} label="Type" value={type} onChange={setType} options={TYPE_OPTIONS} allowEmpty emptyText="Any type" />
        <SelectInput id={`${id}-rel`} label="Religious" value={religious} onChange={setReligious} options={RELIGIOUS_OPTIONS} allowEmpty emptyText="Any" />
        <NumberInput id={`${id}-enr`} label="Min enrollment" value={minEnrollment} onChange={setMinEnrollment} integer />
        <Button id={`${id}-find`} text="Find" icon="search" onClick={() => void run()} loading={loading} />
      </form>

      {results && (
        <>
          <div className="meta">
            {results.length} school{results.length === 1 ? '' : 's'} found
          </div>
          <DataGrid<SchoolSummary>
            rows={results}
            columns={columns}
            loading={loading}
            getRowId={(r) => r.nid}
            onRowClick={(r) => {
              onClose();
              navigate(`/highschools/${encodeURIComponent(r.nid)}`);
            }}
            height={320}
          />
        </>
      )}
    </Modal>
  );
}
